import React from 'react';
import { Link } from 'react-router-dom';

/**
 * SolutionsPage component for the AgentNexus Healthcare AI Platform
 * Showcases the three main Videxa-integrated solutions:
 * - Revenue Cycle Insights
 * - Verbal Order Authentication  
 * - Clinical Documentation Improvement (CDI)
 */
const SolutionsPage = () => {
  return (
    <div className="solutions-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Healthcare <span className="text-primary-300">Solutions</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Comprehensive AI-powered solutions that transform healthcare operations, 
              improve patient outcomes, and optimize revenue cycles across your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Explore Solutions
              </button>
              <button className="border-2 border-primary-300 text-primary-300 hover:bg-primary-300 hover:text-primary-900 px-8 py-4 rounded-lg font-semibold transition-colors">
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Impact Statistics */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            Proven Results Across Healthcare Organizations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">65%</div>
              <p className="text-gray-600 dark:text-gray-300">Reduction in Denial Rates</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">99%</div>
              <p className="text-gray-600 dark:text-gray-300">Coding Accuracy</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">70%</div>
              <p className="text-gray-600 dark:text-gray-300">Documentation Time Reduction</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">24/7</div>
              <p className="text-gray-600 dark:text-gray-300">AI-Powered Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-white">
              Our Solution Portfolio
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Three comprehensive solutions designed to address the most critical challenges 
              in healthcare operations, from revenue cycle optimization to clinical documentation excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Revenue Cycle Insights Solution */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
                <h3 className="text-2xl font-bold text-white mb-2">Revenue Cycle Insights</h3>
                <p className="text-blue-100">Real-time KPI visibility and actionable insights</p>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Key Benefits:</h4>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Industry-leading KPI tracking across all revenue cycle functions
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      AI-powered predictive analytics for proactive decision making
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Real-time operational insights and benchmarking
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Comprehensive financial management and claims analytics
                    </li>
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">22%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Revenue Improvement</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">94%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Claims Acceptance</div>
                  </div>
                </div>
                <Link 
                  to="/solutions/revenue-cycle-insights"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 rounded-lg font-semibold transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Verbal Order Authentication Solution */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
                <h3 className="text-2xl font-bold text-white mb-2">Verbal Order Authentication</h3>
                <p className="text-green-100">Streamlined verbal order management and compliance</p>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Key Benefits:</h4>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Fully automated verbal order processing and authentication
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Seamless EMR integration with real-time data synchronization
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Mobile application for enhanced workflow efficiency
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Reduced administrative burden and compliance risks
                    </li>
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">100%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Automated Processing</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">85%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Time Savings</div>
                  </div>
                </div>
                <Link 
                  to="/solutions/verbal-order-authentication"
                  className="block w-full bg-green-600 hover:bg-green-700 text-white text-center py-3 rounded-lg font-semibold transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Clinical Documentation Improvement (CDI) Solution */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
                <h3 className="text-2xl font-bold text-white mb-2">Clinical Documentation Improvement</h3>
                <p className="text-purple-100">AI-driven documentation excellence and coding accuracy</p>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Key Benefits:</h4>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      AI-driven clinical documentation analysis and optimization
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Advanced coding accuracy with real-time validation
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Comprehensive analytics and performance tracking
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Seamless workflow integration and physician satisfaction
                    </li>
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">45%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Case Mix Improvement</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">95%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Physician Satisfaction</div>
                  </div>
                </div>
                <Link 
                  to="/solutions/cdi"
                  className="block w-full bg-purple-600 hover:bg-purple-700 text-white text-center py-3 rounded-lg font-semibold transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose AgentNexus */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white">
              Why Choose AgentNexus Solutions?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-primary-100 dark:bg-primary-900 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600 dark:text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Proven Results</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Demonstrated improvements across key healthcare metrics with measurable ROI and enhanced patient outcomes.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-primary-100 dark:bg-primary-900 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600 dark:text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">AI-Powered Intelligence</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Advanced artificial intelligence and machine learning algorithms provide predictive insights and automated decision support.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-primary-100 dark:bg-primary-900 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600 dark:text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Seamless Integration</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Designed to integrate seamlessly with existing healthcare systems and workflows without disruption to operations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Healthcare Operations?
          </h2>
          <p className="text-xl mb-8 text-primary-100 max-w-3xl mx-auto">
            Discover how AgentNexus solutions can optimize your revenue cycle, improve documentation accuracy, 
            and enhance operational efficiency across your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors">
              Schedule Demo
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-4 rounded-lg font-semibold transition-colors">
              Contact Sales
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage; 
